import { apiRequest, axiosClient } from './apiClient';
import { getOAuthCallbackUrl } from '@/utils/pkceUtils';
import { ApiError } from '@/models/errors/ApiError';
import type { AuthUser } from '@/permissions';

export interface AccountProfile {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    timezone: string | null;
    avatarUrl: string;
}

interface AvatarApiData {
    url: string;
    [key: string]: unknown;
}

interface AccountProfileApiResponse {
    data: {
        first_name: string;
        last_name: string;
        email: string;
        phone: string | null;
        timezone: string | null;
        avatar: AvatarApiData | null;
    };
}

function mapAccountProfile(data: AccountProfileApiResponse['data']): AccountProfile {
    return {
        firstName: data.first_name,
        lastName: data.last_name,
        email: data.email,
        phone: data.phone ?? '',
        timezone: data.timezone ?? null,
        avatarUrl: data.avatar?.url ?? '',
    };
}

export interface GeneralInformationInput {
    firstName: string;
    lastName: string;
    phone: string;
}

export interface TimezoneInput {
    timezone: string | null;
}

export const getAccount = async (): Promise<AccountProfile> => {
    const response = await apiRequest<AccountProfileApiResponse>('/api/account', 'GET');
    return mapAccountProfile(response.data);
};

export const updateGeneralInformation = async (data: GeneralInformationInput): Promise<AccountProfile> => {
    try {
        const response = await apiRequest<AccountProfileApiResponse>('/api/account/general-information', 'PATCH', {
            body: {
                first_name: data.firstName,
                last_name: data.lastName,
                phone: data.phone || null,
            },
        });
        return mapAccountProfile(response.data);
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError?.status === 422) {
            throw apiError.toValidationError({ first_name: 'firstName', last_name: 'lastName' });
        }
        throw error;
    }
};

export const updateTimezone = async (data: TimezoneInput): Promise<AccountProfile> => {
    const response = await apiRequest<AccountProfileApiResponse>('/api/account/timezone', 'PATCH', {
        body: {
            timezone: data.timezone,
        },
    });
    return mapAccountProfile(response.data);
};

export const changePassword = async (payload: {
    currentPassword: string;
    newPassword: string;
    newPasswordConfirmation: string;
}): Promise<void> => {
    try {
        await apiRequest('/api/account/password', 'PATCH', {
            body: {
                current_password: payload.currentPassword,
                password: payload.newPassword,
                password_confirmation: payload.newPasswordConfirmation,
            },
        });
    } catch (error) {
        const apiError = ApiError.from(error);
        if (apiError?.status === 422) {
            throw apiError.toValidationError({
                current_password: 'current',
                password: 'newPassword',
            });
        }
        throw error;
    }
};

export const uploadAvatar = async (file: File): Promise<AccountProfile> => {
    const formData = new FormData();
    formData.append('avatar_file', file);
    const response = await axiosClient.post<AccountProfileApiResponse>('/api/account/avatar', formData);
    return mapAccountProfile(response.data.data);
};

export const deleteAvatar = async (): Promise<AccountProfile> => {
    const response = await apiRequest<AccountProfileApiResponse>('/api/account/avatar', 'DELETE');
    return mapAccountProfile(response.data);
};
const { VUE_APP_LARAVEL_CLIENT_ID: LARAVEL_CLIENT_ID } = process.env;
const { VUE_APP_API_BASE_URL: API_BASE_URL } = process.env;

export const signIn = async (code: string, codeVerifier: string) => {
    const response = await apiRequest<{ Message: string }>('/api/auth/exchange', 'POST', {
        body: {
            code,
            code_verifier: codeVerifier,
            client_id: LARAVEL_CLIENT_ID,
            grant_type: 'authorization_code',
            redirect_uri: getOAuthCallbackUrl(),
        },
        skipAuthRetry: true,
    });

    return response;
}

export const refreshSession = async () => {
    const response = await apiRequest<{ refreshed: boolean }>('/api/refresh', 'POST', {
        body: {
            client_id: LARAVEL_CLIENT_ID,
        },
        skipAuthRetry: true,
    });
    return response;
}

export const getCurrentUser = async () => {
    const response = await apiRequest<{ user: AuthUser }>('/api/user', 'GET');
    return response;
}

export const logout = async (withRedirect: boolean = true) => {
    const response = await apiRequest<{ message: string }>('/api/logout', 'POST', {
        skipAuthRetry: true,
    });

    const redirectTo = `${window.location.origin}/#/sign-in`;
    const logoutUrl = `${API_BASE_URL}/logout?redirect=${encodeURIComponent(redirectTo)}`;
    if (withRedirect) {
        window.location.assign(logoutUrl);
    }

    return response;
}

export const remindPassword = async (email: string) => {
    await new Promise((resolve) => {
        setTimeout(resolve, 1000);
    });
    return undefined;
}
